<!-- Navigation Bar -->
<nav class="fixed top-0 w-full bg-white shadow-sm z-50 animate-slide-down">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex justify-between items-center h-16">
      <div class="flex items-center">
        <span class="text-2xl font-bold text-blue-600">EnClaro</span>
      </div>
    </div>
  </div>
</nav>

<!-- Main Content -->
<main class="pt-16">
  <!-- Hero Section -->
  <section class="bg-linear-to-br from-blue-50 to-cyan-50 py-24 md:py-32">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center">
        <div class="inline-block mb-6 opacity-0 animate-fade-in-up">
          <span class="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
            Próximamente
          </span>
        </div>
        <h1 class="text-5xl md:text-6xl font-bold mb-6 opacity-0 animate-fade-in-up animation-delay-200 bg-linear-to-r from-blue-600 via-cyan-600 to-blue-700 text-white px-8 py-6 rounded-2xl inline-block">
          Hacemos el Congreso de Chile más claro y accesible para todos
        </h1>
        <p class="text-lg md:text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed opacity-0 animate-fade-in-up animation-delay-400">
          Transformamos leyes, debates legislativos y registros de votación en información clara y estructurada,
          para que cada ciudadano pueda entender qué se discute, cómo se toman las decisiones y cómo actúan sus representantes.
        </p>
      </div>
    </div>
  </section>

  <!-- Mission Values -->
  <section class="py-16 md:py-24 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="text-3xl md:text-4xl font-semibold text-center text-slate-900 mb-12" animateOnScroll>
        Nuestra Misión
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <!-- Claridad -->
        <div class="text-center p-6 transition-transform hover:scale-105 duration-200" animateOnScroll>
          <div class="text-5xl mb-4 inline-block hover:animate-bounce">💡</div>
          <h3 class="text-xl font-semibold text-slate-900 mb-3">Claridad</h3>
          <p class="text-base text-slate-600 leading-relaxed">
            Presentamos la información legislativa de forma simple y comprensible, sin jerga innecesaria.
          </p>
        </div>

        <!-- Contexto -->
        <div class="text-center p-6 transition-transform hover:scale-105 duration-200" animateOnScroll [delay]="150">
          <div class="text-5xl mb-4 inline-block hover:animate-bounce">📖</div>
          <h3 class="text-xl font-semibold text-slate-900 mb-3">Contexto</h3>
          <p class="text-base text-slate-600 leading-relaxed">
            Proporcionamos el contexto necesario para entender el impacto y la historia de cada decisión legislativa.
          </p>
        </div>

        <!-- Transparencia -->
        <div class="text-center p-6 transition-transform hover:scale-105 duration-200" animateOnScroll [delay]="300">
          <div class="text-5xl mb-4 inline-block hover:animate-bounce">👁️</div>
          <h3 class="text-xl font-semibold text-slate-900 mb-3">Transparencia</h3>
          <p class="text-base text-slate-600 leading-relaxed">
            Nuestra misión es mostrar la información pública tal como es, sin persuadir ni juzgar,
            para que tú formes tu propia opinión informada.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- Features Section -->
  <section class="bg-slate-50 py-16 md:py-24">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="text-3xl md:text-4xl font-semibold text-center text-slate-900 mb-4" animateOnScroll>
        Qué Ofreceremos
      </h2>
      <p class="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto" animateOnScroll [delay]="100">
        EnClaro proporcionará herramientas para explorar y entender el trabajo del Congreso de Chile
      </p>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <!-- Feature 1 -->
        <div class="bg-white p-6 rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200" animateOnScroll [delay]="0">
          <h3 class="text-lg font-semibold text-slate-900 mb-2">Leyes y Proyectos de Ley</h3>
          <p class="text-base text-slate-600">
            Accede a todos los proyectos legislativos en curso y aprobados, con resúmenes claros y comprensibles.
          </p>
        </div>

        <!-- Feature 2 -->
        <div class="bg-white p-6 rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200" animateOnScroll [delay]="100">
          <h3 class="text-lg font-semibold text-slate-900 mb-2">Debates Legislativos</h3>
          <p class="text-base text-slate-600">
            Lee transcripciones y resúmenes de los debates que dan forma a las leyes de Chile.
          </p>
        </div>

        <!-- Feature 3 -->
        <div class="bg-white p-6 rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200" animateOnScroll [delay]="200">
          <h3 class="text-lg font-semibold text-slate-900 mb-2">Registros de Votación</h3>
          <p class="text-base text-slate-600">
            Conoce cómo votó cada representante en cada proyecto de ley y decisión importante.
          </p>
        </div>

        <!-- Feature 4 -->
        <div class="bg-white p-6 rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200" animateOnScroll [delay]="300">
          <h3 class="text-lg font-semibold text-slate-900 mb-2">Perfiles de Representantes</h3>
          <p class="text-base text-slate-600">
            Información completa sobre diputados y senadores, su trayectoria y posiciones.
          </p>
        </div>

        <!-- Feature 5 -->
        <div class="bg-white p-6 rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200" animateOnScroll [delay]="400">
          <h3 class="text-lg font-semibold text-slate-900 mb-2">Análisis Contextual</h3>
          <p class="text-base text-slate-600">
            Entendimiento histórico y social de las decisiones legislativas y su impacto.
          </p>
        </div>

        <!-- Feature 6 -->
        <div class="bg-white p-6 rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200" animateOnScroll [delay]="500">
          <h3 class="text-lg font-semibold text-slate-900 mb-2">Búsqueda Inteligente</h3>
          <p class="text-base text-slate-600">
            Encuentra rápidamente la información que necesitas sobre leyes, debates y representantes.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- Coming Soon Section -->
  <section class="py-16 md:py-24 bg-white">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 class="text-3xl md:text-4xl font-semibold text-slate-900 mb-6" animateOnScroll>
        Estamos Construyendo Algo Importante
      </h2>
      <p class="text-lg text-slate-600 mb-8 leading-relaxed" animateOnScroll [delay]="100">
        EnClaro está en desarrollo activo. Estamos trabajando para lanzar una plataforma
        que hará el Congreso de Chile verdaderamente accesible.
      </p>
      <div class="bg-slate-50 p-8 rounded-lg animate-pulse-subtle" animateOnScroll [delay]="200">
        <h3 class="text-xl font-semibold text-slate-900 mb-4">
          Mantente informado sobre nuestro lanzamiento
        </h3>
        <div class="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <input
            type="email"
            placeholder="tu@email.com"
            class="px-4 py-3 border border-slate-300 rounded-lg w-full sm:w-80 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            disabled
          />
          <button
            class="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 w-full sm:w-auto focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 outline-none cursor-not-allowed opacity-60"
            disabled
          >
            Notificarme
          </button>
        </div>
        <p class="text-sm text-slate-500 mt-4">
          Funcionalidad próximamente disponible
        </p>
      </div>
    </div>
  </section>
</main>

<!-- Footer -->
<footer class="bg-slate-800 text-slate-300 py-12" animateOnScroll>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
      <div>
        <h3 class="text-xl font-bold text-white mb-3">EnClaro</h3>
        <p class="text-slate-400 leading-relaxed">
          Haciendo el Congreso de Chile más transparente y accesible para todos los ciudadanos.
        </p>
      </div>
      <div>
        <h4 class="text-lg font-semibold text-white mb-3">Enlaces</h4>
        <div class="flex flex-wrap gap-4">
          <a href="#" class="text-slate-400 hover:text-white transition-colors duration-200">
            Sobre Nosotros
          </a>
          <span class="text-slate-600">|</span>
          <a href="#" class="text-slate-400 hover:text-white transition-colors duration-200">
            Contacto
          </a>
          <span class="text-slate-600">|</span>
          <a href="#" class="text-slate-400 hover:text-white transition-colors duration-200">
            Privacidad
          </a>
          <span class="text-slate-600">|</span>
          <a href="#" class="text-slate-400 hover:text-white transition-colors duration-200">
            Términos de Uso
          </a>
        </div>
      </div>
    </div>
    <div class="border-t border-slate-700 pt-8 text-center text-slate-400">
      <p>&copy; 2026 EnClaro. Haciendo el Congreso de Chile más transparente y accesible.</p>
    </div>
  </div>
</footer>

<!-- Keep for future routing -->
<router-outlet />
